import React, { useMemo, useState, useCallback } from 'react';

/**
 * Click-to-sort for the list tables.
 *
 * Two pieces, kept deliberately small so a page can adopt them without being
 * rewritten: a hook that sorts the rows it is handed, and a header cell that shows
 * which column is driving the order.
 *
 *   const sort = useTableSort(filtered);
 *   ...
 *   <SortTh sort={sort} field="po_number">PO Number</SortTh>
 *   ...
 *   {sort.rows.map(...)}
 *
 * The hook never mutates what it is given, so a page can keep filtering and paging
 * exactly as it did.
 */

/** Numbers sort as numbers, dates as dates, everything else as text. */
function compareValues(a, b) {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  // Blanks go last whichever way the column is pointing, because a column of dashes
  // at the top is never what the reader wanted.
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  const aNum = typeof a === 'number' ? a : Number(String(a).replace(/,/g, ''));
  const bNum = typeof b === 'number' ? b : Number(String(b).replace(/,/g, ''));
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;

  const aTime = Date.parse(a);
  const bTime = Date.parse(b);
  if (Number.isFinite(aTime) && Number.isFinite(bTime)) return aTime - bTime;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function useTableSort(rows, initialField = null, initialDir = 'asc') {
  const [field, setField] = useState(initialField);
  const [dir, setDir] = useState(initialDir);

  // Plain reads rather than nested updaters: setting one piece of state from inside
  // another's updater runs twice under StrictMode and the direction would skip.
  const toggle = useCallback((f) => {
    if (field !== f) { setField(f); setDir('asc'); return; }
    if (dir === 'asc') { setDir('desc'); return; }
    // Third click clears it and gives the page's own order back, which is usually
    // meaningful - newest first, or the order SAP sent.
    setField(null);
    setDir('asc');
  }, [field, dir]);

  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!field) return list;
    const copy = [...list];
    copy.sort((x, y) => {
      const r = compareValues(x?.[field], y?.[field]);
      return dir === 'desc' ? -r : r;
    });
    return copy;
  }, [rows, field, dir]);

  return { rows: sorted, field, dir, toggle };
}

/** A header cell that sorts, and shows which way. */
export function SortTh({ sort, field, children, className = '', align = 'left' }) {
  const active = sort?.field === field;
  const dir = active ? sort.dir : null;
  const alignCls = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => sort?.toggle(field)}
        className={`group inline-flex items-center gap-1 w-full ${alignCls}
                    hover:text-sap-blue transition-colors select-none`}
        title={active
          ? (dir === 'asc' ? 'Sorted A→Z. Click for Z→A' : 'Sorted Z→A. Click to clear')
          : 'Click to sort'}
      >
        <span>{children}</span>
        {/* Both arrows, with the live one darkened: a single arrow that appears only
            once a column is sorted gives the reader nothing to aim at. */}
        <span className="inline-flex flex-col leading-none shrink-0" aria-hidden>
          <svg width="7" height="4" viewBox="0 0 8 5"
               className={active && dir === 'asc' ? 'text-sap-blue' : 'text-gray-300 group-hover:text-gray-400'}>
            <path d="M4 0 L8 5 L0 5 Z" fill="currentColor" />
          </svg>
          <svg width="7" height="4" viewBox="0 0 8 5" className={`mt-[1px] rotate-180 ${
            active && dir === 'desc' ? 'text-sap-blue' : 'text-gray-300 group-hover:text-gray-400'}`}>
            <path d="M4 0 L8 5 L0 5 Z" fill="currentColor" />
          </svg>
        </span>
      </button>
    </th>
  );
}
